/**
 * Iter147 — Field Map module.
 *
 * Unified case-location map: pulls every location source (Locations tab,
 * Vehicles tab, Subject tab) from /api/field-map/locations, filters by
 * case / source, and lets investigators launch Google Maps or Apple Maps
 * directions straight to the selected marker.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import MapErrorBanner from '../components/MapErrorBanner';
import { BACKEND_URL } from '../config/canonical';
import {
  MapPin, Car, User as UserIcon, Search, RefreshCw, Navigation, ExternalLink,
  AlertTriangle, Map as MapIcon, X,
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 32.7767, lng: -96.7970 };

const SOURCE_META = {
  LOCATION: { color: '#2563EB', label: 'Location', Icon: MapPin },
  VEHICLE:  { color: '#F59E0B', label: 'Vehicle',  Icon: Car },
  SUBJECT:  { color: '#8B5CF6', label: 'Subject',  Icon: UserIcon },
};

function pinIcon(src) {
  // Return a Google Maps symbol icon so markers are color-coded by source.
  const color = SOURCE_META[src]?.color || '#1D4ED8';
  if (!window.google?.maps) return null;
  return {
    path: 'M12 2C8 2 5 5 5 9c0 5.5 7 13 7 13s7-7.5 7-13c0-4-3-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z',
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#0B0F19',
    strokeWeight: 1.2,
    scale: 1.6,
    anchor: new window.google.maps.Point(12, 22),
  };
}

function makeNavUrls(loc) {
  const hasCoords = typeof loc.latitude === 'number' && typeof loc.longitude === 'number';
  const dest = hasCoords ? `${loc.latitude},${loc.longitude}` : (loc.address || '');
  const q = encodeURIComponent(dest);
  const google = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${q}`
    : `https://www.google.com/maps/search/?api=1&query=${q}`;
  const apple = hasCoords
    ? `https://maps.apple.com/?daddr=${q}`
    : `https://maps.apple.com/?q=${q}`;
  return { google, apple, hasDestination: Boolean(dest) };
}

export default function FieldMap() {
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'nx-field-map-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [caseFilter, setCaseFilter] = useState('ALL');
  const [selected, setSelected] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/field-map/locations`, { headers });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d?.detail || `HTTP ${r.status}`);
        return;
      }
      const data = await r.json();
      setLocations(data.locations || []);
      setUnmapped(data.unmapped || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const caseOptions = useMemo(() => {
    const map = new Map();
    [...locations, ...unmapped].forEach(l => {
      if (l.case_id && !map.has(l.case_id)) {
        map.set(l.case_id, l.case_title || l.case_number || l.case_id);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [locations, unmapped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (l) => {
      if (sourceFilter !== 'ALL' && l.source !== sourceFilter) return false;
      if (caseFilter !== 'ALL' && l.case_id !== caseFilter) return false;
      if (q) {
        const hay = [l.label, l.address, l.case_title, l.case_number, l.license_plate]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
    return { mapped: locations.filter(match), unmapped: unmapped.filter(match) };
  }, [locations, unmapped, search, sourceFilter, caseFilter]);

  const centerOnFirst = useCallback(() => {
    if (!mapRef || !filtered.mapped.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    filtered.mapped.forEach(l => bounds.extend({ lat: l.latitude, lng: l.longitude }));
    mapRef.fitBounds(bounds, 48);
  }, [mapRef, filtered.mapped]);

  useEffect(() => { if (isLoaded) centerOnFirst(); }, [isLoaded, centerOnFirst]);

  if (loadError || !GOOGLE_MAPS_API_KEY) {
    return (
      <div className="space-y-3">
        <MapErrorBanner message="Google Maps failed to load. Check REACT_APP_GOOGLE_MAPS_API_KEY and provider billing." />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="field-map-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#2563EB]/20 border border-[#2563EB]/40">
            <MapIcon className="w-6 h-6 text-[#60A5FA]" />
          </div>
          <div>
            <div className="nx-eyebrow">Field Operations</div>
            <h1 className="nx-h1 mt-1">Field Map</h1>
            <p className="nx-body mt-1">Every case location, vehicle, and subject address on one live map.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#131A2A] border border-[#334155] text-[#CBD5E1]" data-testid="fm-count-mapped">
            {filtered.mapped.length} mapped
          </Badge>
          {unmapped.length > 0 && (
            <Badge className="bg-amber-500/15 border border-amber-500/40 text-amber-200" data-testid="fm-count-unmapped">
              {filtered.unmapped.length} unmapped
            </Badge>
          )}
          <Button variant="outline" onClick={load}
                  className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" data-testid="fm-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardContent className="p-3 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Search case, address, subject, vehicle, plate…"
                   className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] pl-8"
                   data-testid="fm-search" />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] md:w-44" data-testid="fm-source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="ALL">All sources</SelectItem>
              <SelectItem value="LOCATION">Locations tab</SelectItem>
              <SelectItem value="VEHICLE">Vehicles tab</SelectItem>
              <SelectItem value="SUBJECT">Subjects tab</SelectItem>
            </SelectContent>
          </Select>
          <Select value={caseFilter} onValueChange={setCaseFilter}>
            <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] md:w-56" data-testid="fm-case-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[280px]">
              <SelectItem value="ALL">All cases</SelectItem>
              {caseOptions.map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || sourceFilter !== 'ALL' || caseFilter !== 'ALL') && (
            <Button variant="ghost" onClick={() => { setSearch(''); setSourceFilter('ALL'); setCaseFilter('ALL'); }}
                    className="text-[#94A3B8]" data-testid="fm-clear">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Map */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <Card className="bg-[#131A2A] border-[#334155] overflow-hidden">
          <CardContent className="p-0 h-[65vh] min-h-[480px] relative">
            {!isLoaded ? (
              <div className="h-full flex items-center justify-center text-[#94A3B8]">
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading map…
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={DEFAULT_CENTER}
                zoom={6}
                onLoad={setMapRef}
                options={{ streetViewControl: true, mapTypeControl: true, fullscreenControl: true, gestureHandling: 'greedy' }}
              >
                {filtered.mapped.map(l => (
                  <Marker
                    key={l.id}
                    position={{ lat: l.latitude, lng: l.longitude }}
                    icon={pinIcon(l.source)}
                    onClick={() => setSelected(l)}
                  />
                ))}
                {selected && (
                  <InfoWindow
                    position={{ lat: selected.latitude, lng: selected.longitude }}
                    onCloseClick={() => setSelected(null)}
                  >
                    <div className="min-w-[240px] max-w-[280px] text-slate-900">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                        {SOURCE_META[selected.source]?.label || selected.source}
                      </div>
                      <div className="font-semibold mt-0.5">{selected.label}</div>
                      {selected.address && <div className="text-sm text-slate-700 mt-0.5">{selected.address}</div>}
                      <div className="text-[11px] text-slate-600 mt-1 border-t border-slate-200 pt-1">
                        {selected.case_title || selected.case_number} · {selected.case_number}
                        {selected.case_status && <> · <span className="font-medium">{selected.case_status}</span></>}
                      </div>
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {(() => {
                          const n = makeNavUrls(selected);
                          return (
                            <>
                              <a href={n.google} target="_blank" rel="noreferrer"
                                 className="text-[11px] px-2 py-1 rounded bg-blue-600 text-white font-medium">
                                Google Maps
                              </a>
                              <a href={n.apple} target="_blank" rel="noreferrer"
                                 className="text-[11px] px-2 py-1 rounded bg-slate-900 text-white font-medium">
                                Apple Maps
                              </a>
                              {selected.case_id && (
                                <button onClick={() => navigate(`/cases/${selected.case_id}`)}
                                        className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white font-medium">
                                  View Case
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </CardContent>
        </Card>

        {/* Right panel */}
        <div className="space-y-3">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardContent className="p-3 max-h-[65vh] overflow-y-auto">
              {loading && <div className="text-sm text-[#94A3B8] p-2">Loading locations…</div>}
              {!loading && error && (
                <div className="text-sm text-red-300 p-2 border border-red-500/30 bg-red-500/10 rounded">
                  <AlertTriangle className="inline w-4 h-4 mr-1" /> {error}
                </div>
              )}
              {!loading && !error && filtered.mapped.length === 0 && filtered.unmapped.length === 0 && (
                <div className="text-sm text-[#94A3B8] p-6 text-center">
                  No case locations match the current filters.
                </div>
              )}
              <ul className="space-y-2">
                {filtered.mapped.map(l => {
                  const Icon = SOURCE_META[l.source]?.Icon || MapPin;
                  return (
                    <li key={l.id}>
                      <button
                        onClick={() => {
                          setSelected(l);
                          if (mapRef) mapRef.panTo({ lat: l.latitude, lng: l.longitude });
                        }}
                        className={`w-full text-left rounded border p-2 transition-colors ${selected?.id === l.id ? 'border-blue-500/60 bg-blue-500/10' : 'border-[#334155] bg-[#0B0F19] hover:border-[#3B82F6]/40'}`}
                        data-testid={`fm-row-${l.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 mt-0.5" style={{ color: SOURCE_META[l.source]?.color }} />
                          <div className="min-w-0 flex-1">
                            <div className="text-[#F8FAFC] font-medium truncate">{l.label}</div>
                            {l.address && <div className="text-[11px] text-[#94A3B8] truncate">{l.address}</div>}
                            <div className="text-[10px] text-[#64748B] font-mono mt-0.5">
                              {l.case_number} · {l.case_status || '—'}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {filtered.unmapped.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#334155]">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Unmapped ({filtered.unmapped.length})
                  </div>
                  <ul className="space-y-1.5">
                    {filtered.unmapped.map(l => (
                      <li key={l.id} className="rounded border border-amber-500/20 bg-amber-500/5 p-2"
                          data-testid={`fm-unmapped-${l.id}`}>
                        <div className="text-[#F8FAFC] font-medium text-sm truncate">{l.label}</div>
                        {l.address && (
                          <div className="text-[11px] text-[#94A3B8]">
                            {l.address}
                            {' · '}
                            <a href={makeNavUrls(l).google} target="_blank" rel="noreferrer"
                               className="text-blue-300 inline-flex items-center">
                              Google <ExternalLink className="w-3 h-3 ml-0.5" />
                            </a>
                          </div>
                        )}
                        <div className="text-[10px] text-[#64748B] font-mono mt-0.5">
                          {l.case_number} · needs geocoding
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile floating directions bar for the selected marker */}
      {selected && (
        <div className="lg:hidden fixed bottom-16 left-2 right-2 z-40 rounded-lg border border-[#334155] bg-[#0B0F19]/95 backdrop-blur p-3 shadow-xl"
             data-testid="fm-mobile-selected">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[#F8FAFC] font-semibold truncate">{selected.label}</div>
              <div className="text-[11px] text-[#94A3B8] truncate">{selected.address || '—'}</div>
              <div className="text-[10px] text-[#64748B] font-mono">{selected.case_number} · {selected.case_status || '—'}</div>
            </div>
            <button onClick={() => setSelected(null)} className="text-[#94A3B8]"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2 mt-2">
            {(() => {
              const n = makeNavUrls(selected);
              return (
                <>
                  <a href={n.google} target="_blank" rel="noreferrer"
                     className="flex-1 text-center text-xs px-2 py-2 rounded bg-blue-600 text-white font-semibold">
                    <Navigation className="w-3.5 h-3.5 inline mr-1" /> Google
                  </a>
                  <a href={n.apple} target="_blank" rel="noreferrer"
                     className="flex-1 text-center text-xs px-2 py-2 rounded bg-slate-900 text-white font-semibold border border-slate-700">
                    <Navigation className="w-3.5 h-3.5 inline mr-1" /> Apple
                  </a>
                  {selected.case_id && (
                    <button onClick={() => navigate(`/cases/${selected.case_id}`)}
                            className="flex-1 text-xs px-2 py-2 rounded bg-emerald-600 text-white font-semibold">
                      View Case
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
